import { useState, useEffect } from "react";
import { getEmployees } from "../services/employeesApi";
import { Employee } from "../types/employee";
import { useToast } from "./useToast";
import { useAuth } from "../auth/AuthContext";

const STAFF_COLORS = [
  "#885ABB",
  "#5AB7BB",
  "#C96EBE",
  "#837EED",
  "#FFB74D",
  "#4DB6AC",
  "#E57373",
];

function isEmployeeUser(user: any): boolean {
  const roles = user?.roles || [];
  const isAdmin = roles.some((r: any) => r?.name === "administrator");
  const isEmployee = roles.some((r: any) => r?.name === "employee");
  return isEmployee && !isAdmin;
}

/** Roles that can appear as calendar / booking columns. */
function isBookableStaffRole(role: string | null | undefined): boolean {
  const r = (role || "").toLowerCase().trim();
  if (!r) return true; // keep if API omitted role
  // Match role name or display_name variants
  if (r === "employee" || r.includes("employee")) return true;
  if (r === "administrator" || r.includes("admin")) return true;
  // Exclude customers / superadmin noise if ever present
  if (r.includes("customer") || r.includes("superadmin") || r === "agent") {
    return false;
  }
  return true;
}

export const useEmployees = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [staffList, setStaffList] = useState<
    { id: number; name: string; color: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        setLoading(true);
        const data = await getEmployees();
        setEmployees(data);

        // Calendar needs everyone who can take appointments (employee + administrator).
        let mappedStaff = data
          .filter((emp) => isBookableStaffRole(emp.role))
          .map((emp, index) => ({
            id: emp.id,
            name: (emp.name || "").trim(),
            color: STAFF_COLORS[index % STAFF_COLORS.length],
          }))
          .filter((s) => s.id > 0 && s.name);

        // Logged-in employee (non-admin): only their own column — not all staff.
        if (isEmployeeUser(user) && user?.id) {
          const selfId = Number(user.id);
          const self = mappedStaff.find((s) => s.id === selfId);
          mappedStaff = self
            ? [self]
            : [
                {
                  id: selfId,
                  name: (user.name || "Me").trim(),
                  color: STAFF_COLORS[0],
                },
              ];
        }

        // If filter wiped everyone but API returned people, show all mapped names
        // (admin / full staff list only — never re-expand for employees).
        if (
          !isEmployeeUser(user) &&
          mappedStaff.length === 0 &&
          data.length > 0
        ) {
          mappedStaff = data
            .map((emp, index) => ({
              id: emp.id,
              name: (emp.name || "").trim(),
              color: STAFF_COLORS[index % STAFF_COLORS.length],
            }))
            .filter((s) => s.id > 0 && s.name);
        }

        setStaffList(mappedStaff);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        if (isEmployeeUser(user) && user?.id) {
          setStaffList([
            {
              id: Number(user.id),
              name: (user.name || "Me").trim(),
              color: STAFF_COLORS[0],
            },
          ]);
          setError(null);
        } else {
          setStaffList([]);
          setError("Failed to load employees");
          toast.error("Failed to load staff members");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeesData();
  }, [user?.id]);

  return { employees, staffList, loading, error };
};
